import React, { useState } from 'react';
import { tickerAPI, matchMetaAPI } from '../../services/api';

function toStr(v) { return (v?._id || v || '').toString(); }

function MiniForm({ title, fields, onSubmit, onClose }) {
    const [vals, setVals] = useState(() => {
        const defaults = {};
        fields.forEach(f => { if (f.defaultValue !== undefined) defaults[f.name] = String(f.defaultValue); });
        return defaults;
    });
    const handleChange = (name, val) => setVals(prev => ({ ...prev, [name]: val }));
    const handleSubmit = (e) => { e.preventDefault(); onSubmit(vals); };

    const renderSelect = (f) => {
        // Check if options have groups (array of { label, options[] })
        const hasGroups = f.options?.[0]?.options !== undefined;
        return (
            <select
                value={vals[f.name] || ''}
                onChange={e => handleChange(f.name, e.target.value)}
                required={f.required}
            >
                <option value="">— wählen —</option>
                {hasGroups
                    ? f.options.map(grp => (
                        <optgroup key={grp.label} label={grp.label}>
                            {grp.options.map(o => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </optgroup>
                    ))
                    : f.options.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))
                }
            </select>
        );
    };

    return (
        <div className="ticker-modal-backdrop" onClick={onClose}>
            <div className="ticker-modal" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-lg text-white">{title}</h3>
                    <button type="button" className="ticker-action-icon" onClick={onClose}>✕</button>
                </div>
                <form onSubmit={handleSubmit}>
                    {fields.map(f => (
                        <div key={f.name}>
                            <label>{f.label}</label>
                            {f.type === 'select' ? renderSelect(f) : (
                                <input
                                    type={f.type || 'text'}
                                    placeholder={f.placeholder || ''}
                                    value={vals[f.name] || ''}
                                    onChange={e => handleChange(f.name, e.target.value)}
                                    required={f.required}
                                    min={f.min}
                                    max={f.max}
                                />
                            )}
                        </div>
                    ))}
                    <div className="flex gap-2 justify-end mt-2">
                        <button type="button" className="ticker-clock-btn" onClick={onClose}>Abbrechen</button>
                        <button type="submit" className="ticker-clock-btn ticker-clock-btn--go">Speichern</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default function TickerQuickButtons({ gameId, game, lineup, availablePlayers, meta, displayMinute, onCreated, onMetaUpdate, gameFinished }) {
    const [active, setActive] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const homeId = toStr(game?.hid);
    const awayId = toStr(game?.aid);
    const homeName = game?.hid?.name || 'Heim';
    const awayName = game?.aid?.name || 'Gast';

    const teamOptions = [
        { value: homeId, label: homeName },
        { value: awayId, label: awayName },
    ];

    // Look up jersey number from availablePlayers (playertool) by plid
    const getNum = (plid) => {
        if (!availablePlayers) return '';
        const pt = availablePlayers.find(p => toStr(p.plid?._id || p.plid) === toStr(plid));
        return pt?.num ? `#${pt.num} ` : '';
    };

    const playerLabel = (p, teamPrefix) => {
        const plid = p.plid?._id || p.plid;
        const surname = p.plid?.surname || '';
        const prename = p.plid?.prename ? p.plid.prename[0] + '. ' : '';
        const num = getNum(plid);
        return `${teamPrefix}${num}${prename}${surname}`.trim() || `${teamPrefix}–`;
    };

    // Flat list for goal/card selects (all players)
    const allPlayerOptions = [
        ...lineup?.filter(p => toStr(p.tid) === homeId).map(p => ({
            value: toStr(p.plid?._id || p.plid),
            label: playerLabel(p, '[H] '),
            tid: homeId,
        })) || [],
        ...lineup?.filter(p => toStr(p.tid) === awayId).map(p => ({
            value: toStr(p.plid?._id || p.plid),
            label: playerLabel(p, '[G] '),
            tid: awayId,
        })) || [],
    ];

    // Grouped options per team with Starter / Ersatzbank separation
    const buildGroupedOptions = (teamId, prefix) => {
        const starters = lineup?.filter(p => toStr(p.tid) === teamId && p.isStarter !== false) || [];
        const bench    = lineup?.filter(p => toStr(p.tid) === teamId && p.isStarter === false) || [];
        const teamName = teamId === homeId ? homeName : awayName;
        const groups = [];
        if (starters.length) groups.push({
            label: `${prefix} ${teamName} – Startelf`,
            options: starters.map(p => ({ value: toStr(p.plid?._id || p.plid), label: playerLabel(p, '') })),
        });
        if (bench.length) groups.push({
            label: `${prefix} ${teamName} – Ersatzbank`,
            options: bench.map(p => ({ value: toStr(p.plid?._id || p.plid), label: playerLabel(p, '') })),
        });
        return groups;
    };

    // For sub: out = starters of a team, in = bench of a team
    // We provide all teams as grouped options; team selection filters displayed entries
    const allGroupedOptions = [
        ...buildGroupedOptions(homeId, '🏠'),
        ...buildGroupedOptions(awayId, '✈️'),
    ];

    const currentFT = meta?.scoreFT || { home: game?.scoreh ?? 0, away: game?.scorea ?? 0 };

    const post = async (type, minute, text, extra = {}) => {
        setSubmitting(true);
        try {
            const res = await tickerAPI.create({
                gid: gameId,
                type,
                minute: parseInt(minute) || 0,
                half: meta?.currentHalf || 1,
                textRaw: text,
                textPublished: text,
                published: true,
                ...extra
            });
            onCreated(res.data);
        } catch (err) {
            console.error(err);
        } finally {
            setSubmitting(false);
            setActive(null);
        }
    };

    const updateScore = async (scoreData) => {
        try {
            const updated = await matchMetaAPI.update(gameId, { scoreFT: scoreData });
            onMetaUpdate(updated.data);
        } catch (err) { console.error(err); }
    };

    const autoMinute = gameFinished ? 0 : (displayMinute || 0);

    // Resolve player id → clean label (no [H]/[G] prefix, for use in stored text)
    const findPlayer = (id) => {
        const opt = allPlayerOptions.find(o => o.value?.toString() === id?.toString());
        if (!opt) return null;
        return { ...opt, cleanLabel: opt.label.replace(/^\[.\]\s*/, '') };
    };

    const BUTTONS = [
        {
            key: 'goal',
            label: '⚽ Tor',
            color: 'btn-success',
            fields: [
                { name: 'minute', label: 'Minute', type: 'number', min: 0, max: 120, required: true, defaultValue: autoMinute },
                { name: 'team', label: 'Team', type: 'select', options: teamOptions, required: true },
                { name: 'player', label: 'Torschütze', type: 'select', options: allGroupedOptions },
                { name: 'text', label: 'Freitext', type: 'text', placeholder: 'z.B. Traumtor aus 25m' },
            ],
            onSubmit: async (vals) => {
                const scorer = findPlayer(vals.player);
                const teamName = teamOptions.find(o => o.value === vals.team)?.label || '';
                const newScore = { ...currentFT };
                if (vals.team === homeId) newScore.home = (newScore.home || 0) + 1;
                else newScore.away = (newScore.away || 0) + 1;
                const text = `TOR! ${teamName}${scorer ? ` – ${scorer.cleanLabel}` : ''}. Spielstand: ${newScore.home}:${newScore.away}${vals.text ? `. ${vals.text}` : ''}`;
                await updateScore(newScore);
                await post(2, vals.minute, text, { tid: vals.team || null, plid: vals.player || null });
            }
        },
        {
            key: 'yellow',
            label: '🟨 Gelb',
            color: 'btn-warning',
            fields: [
                { name: 'minute', label: 'Minute', type: 'number', min: 0, max: 120, required: true, defaultValue: autoMinute },
                { name: 'team', label: 'Team', type: 'select', options: teamOptions, required: true },
                { name: 'player', label: 'Spieler', type: 'select', options: allGroupedOptions },
            ],
            onSubmit: async (vals) => {
                const player = findPlayer(vals.player);
                const teamName = teamOptions.find(o => o.value === vals.team)?.label || '';
                await post(3, vals.minute, `Gelbe Karte: ${player?.cleanLabel || teamName}`, { tid: vals.team || null, plid: vals.player || null });
            }
        },
        {
            key: 'red',
            label: '🟥 Rot',
            color: 'btn-error',
            fields: [
                { name: 'minute', label: 'Minute', type: 'number', min: 0, max: 120, required: true, defaultValue: autoMinute },
                { name: 'team', label: 'Team', type: 'select', options: teamOptions, required: true },
                { name: 'player', label: 'Spieler', type: 'select', options: allGroupedOptions },
            ],
            onSubmit: async (vals) => {
                const player = findPlayer(vals.player);
                const teamName = teamOptions.find(o => o.value === vals.team)?.label || '';
                await post(4, vals.minute, `Rote Karte: ${player?.cleanLabel || teamName}`, { tid: vals.team || null, plid: vals.player || null });
            }
        },
        {
            key: 'sub',
            label: '🔄 Wechsel',
            color: 'btn-info',
            fields: [
                { name: 'minute', label: 'Minute', type: 'number', min: 0, max: 120, required: true, defaultValue: autoMinute },
                { name: 'team', label: 'Team', type: 'select', options: teamOptions, required: true },
                {
                    name: 'out',
                    label: '⬆️ Raus (Starter)',
                    type: 'select',
                    options: allGroupedOptions,
                },
                {
                    name: 'in',
                    label: '⬇️ Rein (Ersatzbank)',
                    type: 'select',
                    options: allGroupedOptions,
                },
            ],
            onSubmit: async (vals) => {
                const pOut = findPlayer(vals.out);
                const pIn  = findPlayer(vals.in);
                const teamName = teamOptions.find(o => o.value === vals.team)?.label || '';
                await post(5, vals.minute,
                    `Wechsel ${teamName}: ${pOut?.cleanLabel || '?'} ⬆️ / ${pIn?.cleanLabel || '?'} ⬇️`,
                    { tid: vals.team || null, plid: vals.out || null, playerOut: vals.out || null, playerIn: vals.in || null }
                );
            }
        },
    ];

    const QUICK = [
        { key: 'goal',   icon: '⚽', label: 'Tor',     cls: 'ticker-quick-btn--goal' },
        { key: 'yellow', icon: '🟨', label: 'Gelb',    cls: 'ticker-quick-btn--yellow' },
        { key: 'red',    icon: '🟥', label: 'Rot',     cls: 'ticker-quick-btn--red' },
        { key: 'sub',    icon: '🔄', label: 'Wechsel', cls: 'ticker-quick-btn--sub' },
    ];

    const currentBtn = BUTTONS.find(b => b.key === active);

    return (
        <>
            <div className="ticker-quick-bar">
                {QUICK.map(q => (
                    <button
                        key={q.key}
                        type="button"
                        className={`ticker-quick-btn ${q.cls}`}
                        onClick={() => setActive(q.key)}
                        disabled={submitting}
                    >
                        <span className="ticker-quick-btn-icon">{q.icon}</span>
                        <span>{q.label}</span>
                    </button>
                ))}
            </div>

            {currentBtn && (
                <MiniForm
                    title={currentBtn.label}
                    fields={currentBtn.fields}
                    onSubmit={currentBtn.onSubmit}
                    onClose={() => setActive(null)}
                />
            )}
        </>
    );
}
